import Box from "@mui/material/Box"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { GoodsReceiptFilterChips, GoodsReceiptFilterDrawer } from "../../../components"
import { useStoredGoodsReceiptPalletsTable } from "../hooks"

import { StoredGoodsReceiptPalletsTable } from "./StoredGoodsReceiptPalletsTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useGoodsReceiptPalletActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptPalletActions"

export const StoredGoodsReceiptsContainer: FC = () => {
  const {
    goodsReceiptPallets,
    isGoodsReceiptPalletsFetching,
    searchQuery,
    warehouseAreaIds,
    warehouseOptions,
    isWarehousesFetching,
    sortingState,
    tableContainerRef,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleSortingChange,
    handleScroll,
  } = useStoredGoodsReceiptPalletsTable({
    routePath: "/app/warehouse-receiving",
  })

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const navigate = useNavigate()
  const { handleQrPrint } = useGoodsReceiptPalletActions()

  const handleFilterDrawerToggle = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value)
  }

  const handleRowClick = (pallet: GoodsReceiptPallet) => {
    navigate({
      to: `/app/warehouse-receiving/stored/${pallet.goodsReceiptId}`,
    })
  }

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by GR #, PO #, Product ID, Reference #'
          defaultValue={searchQuery}
          onChange={handleSearchInputChange}
          className='w-full max-w-[524px]'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={handleFilterDrawerToggle}
          data-testid='filter-button'
        >
          Filter
        </Button>
      </Box>
      {warehouseAreaIds.length > 0 && (
        <GoodsReceiptFilterChips
          warehouseAreaIds={warehouseAreaIds}
          warehouseOptions={warehouseOptions}
          onRemove={handleFilterRemove}
        />
      )}
      <StoredGoodsReceiptPalletsTable
        goodsReceiptPallets={goodsReceiptPallets}
        isLoading={isGoodsReceiptPalletsFetching}
        sortingState={sortingState}
        tableContainerRef={tableContainerRef}
        onSortingChange={handleSortingChange}
        onQrPrint={handleQrPrint}
        onRowClick={handleRowClick}
        onScroll={handleScroll}
      />
      <GoodsReceiptFilterDrawer
        isOpen={isFilterDrawerOpen}
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        isWarehousesFetching={isWarehousesFetching}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />
    </Box>
  )
}
